import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
} from "react";
import { getUserConnectionsApiV1UsersMeConnectionsGet } from "@/backend-sdk/client/sdk.gen";
import { tenantAtivo } from "@/lib/hey-api-client";
import { useAccount } from "./account.context";
import type { ConnectionInfo } from "@/backend-sdk/client/types.gen";
import type { ConnectionHealth } from "@/services/integration-health.service";

/* eslint-disable react-refresh/only-export-components */

type IntegrationType = "google" | "meta";
type ConnectionStatus =
  | "connected"
  | "disconnected"
  | "pending"
  | "error"
  | "degraded";

export interface Integration {
  id: string;
  type: IntegrationType;
  name: string;
  description: string;
  status: ConnectionStatus;
  isEnabled: boolean;
  health?: ConnectionHealth | null;
  lastSync?: string;
  accountCount?: number;
}

interface IntegrationState {
  integrations: Map<string, Integration>;
  globalHealth: {
    overall: "healthy" | "degraded" | "critical";
    healthyCount: number;
    totalCount: number;
    totalIssues: number;
  };
  isLoading: boolean;
  error: string | null;
  lastGlobalCheck?: string;
}

type IntegrationAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "BULK_UPDATE_INTEGRATIONS"; payload: Integration[] }
  | {
      type: "UPDATE_INTEGRATION";
      payload: { id: string; updates: Partial<Integration> };
    }
  | { type: "UPDATE_GLOBAL_HEALTH"; payload: IntegrationState["globalHealth"] }
  | { type: "SET_LAST_GLOBAL_CHECK"; payload: string };

const initialState: IntegrationState = {
  integrations: new Map(),
  globalHealth: {
    overall: "critical",
    healthyCount: 0,
    totalCount: 0,
    totalIssues: 0,
  },
  isLoading: false,
  error: null,
};

function integrationReducer(
  state: IntegrationState,
  action: IntegrationAction,
): IntegrationState {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "BULK_UPDATE_INTEGRATIONS": {
      const map = new Map<string, Integration>();
      action.payload.forEach((integration) => {
        map.set(integration.id, integration);
      });
      return { ...state, integrations: map };
    }
    case "UPDATE_INTEGRATION": {
      const map = new Map(state.integrations);
      const existing = map.get(action.payload.id);
      if (existing) {
        map.set(action.payload.id, { ...existing, ...action.payload.updates });
      } else {
        // Create new integration if it doesn't exist
        const newIntegration: Integration = {
          id: action.payload.id,
          type: action.payload.id.includes("google") ? "google" : "meta",
          name: action.payload.id.includes("google") ? "Google" : "Meta",
          description: action.payload.id.includes("google")
            ? "Google Analytics, Google Ads e Search Console"
            : "Meta Ads e Instagram Insights",
          status: action.payload.updates.status || "disconnected",
          isEnabled: true,
          ...action.payload.updates,
        };
        map.set(action.payload.id, newIntegration);
      }
      return { ...state, integrations: map };
    }
    case "UPDATE_GLOBAL_HEALTH":
      return { ...state, globalHealth: action.payload };
    case "SET_LAST_GLOBAL_CHECK":
      return { ...state, lastGlobalCheck: action.payload };
    default:
      return state;
  }
}

interface IntegrationContextType {
  state: IntegrationState;
  updateIntegration: (id: string, updates: Partial<Integration>) => void;
  refreshGlobalHealth: () => Promise<void>;
}

const IntegrationContext = createContext<IntegrationContextType | undefined>(
  undefined,
);

interface IntegrationProviderProps {
  children: ReactNode;
}

export const IntegrationProvider: React.FC<IntegrationProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(integrationReducer, initialState);
  // As conexões são da loja ativa. Sem ela nas dependências, a carga rodava uma vez só, e a
  // troca de loja deixava as integrações da anterior no contexto até recarregar a página. A
  // chave é o `customer_uuid` que a consulta manda, como na `key` de `ExigeLojaAtiva`.
  const { selectedAccount } = useAccount();
  const loja = selectedAccount?.customer_uuid || tenantAtivo();

  const calculateGlobalHealth = (integrations?: Integration[]) => {
    const list = integrations ?? Array.from(state.integrations.values());
    const enabled = list.filter((integration) => integration.isEnabled);
    const totalCount = enabled.length;

    if (totalCount === 0) {
      dispatch({
        type: "UPDATE_GLOBAL_HEALTH",
        payload: {
          overall: "critical",
          healthyCount: 0,
          totalCount: 0,
          totalIssues: 0,
        },
      });
      return;
    }

    const healthyCount = enabled.filter(
      (integration) => integration.status === "connected",
    ).length;

    let overall: "healthy" | "degraded" | "critical";
    if (healthyCount === totalCount) overall = "healthy";
    else if (healthyCount > 0) overall = "degraded";
    else overall = "critical";

    dispatch({
      type: "UPDATE_GLOBAL_HEALTH",
      payload: { overall, healthyCount, totalCount, totalIssues: 0 },
    });
  };

  useEffect(() => {
    // Evitar chamada antes da autenticação concluir
    const hasToken = Boolean(localStorage.getItem("access_token"));
    // As integrações da loja anterior saem na hora da troca: enquanto a consulta nova não
    // volta, o contexto não descreve loja nenhuma, em vez de descrever a errada.
    dispatch({ type: "BULK_UPDATE_INTEGRATIONS", payload: [] });
    dispatch({ type: "SET_ERROR", payload: null });
    // Sem loja — a agência na conta principal — não há conexões a consultar, e a rota
    // responderia `400 Conta não informada` com um toast a cada volta para a conta principal.
    // Achado de code review.
    if (!hasToken || !loja) {
      dispatch({ type: "SET_LOADING", payload: false });
      return;
    }

    // Numa troca rápida A → B, a resposta de A pode voltar depois da de B. Quem chega com a
    // loja já trocada é descartado. Achado de code review.
    let descartada = false;

    const load = async () => {
      dispatch({ type: "SET_LOADING", payload: true });

      try {
        const { data: response } =
          await getUserConnectionsApiV1UsersMeConnectionsGet({
            throwOnError: true,
            // <D-062> O estado das conexões é da **loja**. Sem o `customer_uuid` a rota cai
            // na conta ativa do token, que um token de login comum não traz.
            query: { customer_uuid: loja },
          });
        const connections: ConnectionInfo[] = response.connections || [];

        const integrations: Integration[] = [
          {
            id: "google-main",
            type: "google",
            name: "Google",
            description: "Integrações Google (Analytics, Ads, Search Console)",
            status: connections.find((c) => c.source === "google")?.connected
              ? "connected"
              : "disconnected",
            isEnabled: true,
            lastSync:
              connections.find((c) => c.source === "google")?.last_sync ||
              undefined,
            accountCount: connections.find((c) => c.source === "google")
              ?.connected
              ? 1
              : 0,
          },
          {
            id: "meta-main",
            type: "meta",
            name: "Meta",
            description: "Integrações Meta (Ads, Instagram)",
            status: connections.find((c) => c.source === "meta")?.connected
              ? "connected"
              : "disconnected",
            isEnabled: true,
            lastSync:
              connections.find((c) => c.source === "meta")?.last_sync ||
              undefined,
            accountCount: connections.find((c) => c.source === "meta")
              ?.connected
              ? 1
              : 0,
          },
        ];

        if (descartada) return;
        dispatch({ type: "BULK_UPDATE_INTEGRATIONS", payload: integrations });
        calculateGlobalHealth(integrations);
      } catch (error) {
        if (descartada) return;
        dispatch({
          type: "SET_ERROR",
          payload: "Erro ao carregar integrações",
        });
      } finally {
        if (!descartada) dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    load();
    return () => {
      descartada = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, loja]);

  const updateIntegration = (id: string, updates: Partial<Integration>) => {
    dispatch({ type: "UPDATE_INTEGRATION", payload: { id, updates } });
    const list = Array.from(state.integrations.values()).map((integration) =>
      integration.id === id ? { ...integration, ...updates } : integration,
    );
    calculateGlobalHealth(list);
  };

  const refreshGlobalHealth = async () => {
    calculateGlobalHealth();
    dispatch({
      type: "SET_LAST_GLOBAL_CHECK",
      payload: new Date().toISOString(),
    });
  };

  const value: IntegrationContextType = {
    state,
    updateIntegration,
    refreshGlobalHealth,
  };

  return (
    <IntegrationContext.Provider value={value}>
      {children}
    </IntegrationContext.Provider>
  );
};

const useIntegrationContext = (): IntegrationContextType => {
  const context = useContext(IntegrationContext);
  if (!context) {
    throw new Error(
      "useIntegrationContext deve ser usado dentro de IntegrationProvider",
    );
  }
  return context;
};

export { useIntegrationContext };
